import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Tag } from '@shared/interfaces/project/tag.interface';
import { Response } from '@shared/models/response.model';
import { Event } from '@shared/models/event.model';
import { BaseEventsComponent } from '../../components/base-events.component';
import { Error } from '@shared/interfaces/error.interface';
import { PhCropperComponent } from '@shared/components/ph-cropper/ph-cropper.component';
import { PhUploadComponent } from '@shared/components/ph-upload/ph-upload.component';
import { PhCropperOutputFile } from '@shared/components/ph-cropper/ph-cropper-output-file.interface';
import { ActivatedRoute } from '@angular/router';
import { LanguageService } from '@shared/services/language.service';
import { Types } from '@shared/interfaces/event/types.interface';
import { SwiperConfigInterface } from 'ngx-swiper-wrapper';
import { Theme } from '@shared/interfaces/event/theme.interface';
import { EventStatus } from '@shared/interfaces/event/event.interface';
import { Organisation } from '@shared/interfaces/event/organisation.interface';
import { RoutesPath } from '@shared/routes-path';
import * as moment from 'moment';
import { Config } from '@shared/config';
import { AdditionalInfoComponent } from '@layouts/default/layouts/logged/pages/project/pages/create/components/additional-info/additional-info.component';
import { Coordinates } from '@shared/interfaces/project/coordinates.interface';
import { MenuService } from '@shared/services/menu.service';
import OpenStreetMapWithMarkerComponent from '@layouts/default/components/open-street-map-with-marker/open-street-map-with-marker.component';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss'],
})
export class CreateComponent extends BaseEventsComponent implements OnInit {
  @ViewChild('openStreetMap', { static: true }) openStreetMap: OpenStreetMapWithMarkerComponent;
  @ViewChild('additionalInfo', { static: true })
  additionalInfo: AdditionalInfoComponent;
  form: UntypedFormGroup = new UntypedFormGroup({});
  formBeneficent: UntypedFormGroup = new UntypedFormGroup({});
  formImg: UntypedFormGroup = new UntypedFormGroup({});
  types: Types[] = [];
  categories: Tag[] = [];
  categoriesFiltered: Tag[] = [];
  organisations: Organisation[] = [];
  emails: any;
  themes: Theme[] = [];
  event: any;
  isEndDate: boolean = false;
  isBeneficent: boolean = false;
  beneficentEditId: number;
  pickedTheme: any;
  times: any;
  endTimes: any;
  editMode: boolean = false;
  @ViewChild('phCropper', { static: true })
  phCropper: PhCropperComponent;
  @ViewChild('previewImg', { static: true })
  previewImg: PhUploadComponent;
  disabled: boolean = false;
  changeMapOnClick: boolean = true;
  markerDraggable: boolean = true;
  autocompleteEdit: string;
  isWcd = false;
  themesSliderConfig: SwiperConfigInterface = {
    navigation: {
      nextEl: '.swiper-button-next-unique-4',
      prevEl: '.swiper-button-prev-unique-4',
    },
    slidesPerView: 4,
    spaceBetween: 1,
    scrollbar: true,
  };

  configCropper: Cropper.Options = {
    autoCropArea: 0.8,
    viewMode: 1,
    zoomable: true,
    aspectRatio: 2,
    rotatable: true,
    scalable: true,
    dragMode: 'move',
    checkOrientation: true,
  };

  datePickerExtraConfig: object = {
    disableSince: 0,
    disableUntil: this.dayNow,
  };

  datePickerExtraConfigEndAt: any = {
    disableSince: 0,
    disableUntil: {
      year: 1900,
      month: 1,
      day: 1,
    },
  };

  constructor(
    public route: ActivatedRoute,
    public fb: UntypedFormBuilder,
    public languageService: LanguageService,
    navigationService: MenuService
  ) {
    super();
    this.getTags();
    this.getThemes();
    this.getOrganisations();
    this.getTypes();
    this.setTimes();
    this.isWcd = navigationService.isWcd();

    if (this.route.snapshot.data.editEvent) {
      this.enableEditMode();
    }

    this.form.addControl(
      'address',
      this.fb.group({
        name: ['', Validators.required],
        city: [null],
        voivodeship: [null],
        country: [null],
        zipcode: [null],
        street: [null],
        longitude: [null],
        latitude: [null],
      })
    );

    this.form.addControl('googleMapsInput', new UntypedFormControl([null, Validators.required]));
  }

  ngOnInit() {
    if (!this.editMode) {
      this.getCoordinatesFromIp();
      this.previewImg.setRequired();
    }
  }

  typeFormatter(type) {
    switch (type) {
      case 'PublicEvent':
        return 2;
      case 'PrivateEvent':
        return 1;
      default:
        return 2;
    }
  }

  enableEditMode() {
    this.editMode = true;
    const subData = this.route.data.subscribe((data: { editEvent: Response<Event> }) => {
      const event = new Event(data.editEvent.data);

      this.form.addControl('googleMapsInput', new UntypedFormControl([null, Validators.required]));

      this.form.get('googleMapsInput').setValue(event.address.name);

      this.autocompleteEdit = this.form.get('googleMapsInput').value;

      if (event.address.city) {
        this.autocompleteEdit +=
          ', ' +
          event.address.city +
          ', ' +
          this.translate.instant('COUNTRIES.' + event.address.country);
      }

      setTimeout(() => {
        if (event.file) {
          this.formImg.get('previewImg').setValue(Config.HOST + event.file.image);
          this.previewImg.setRequired();
        }
        if (event.theme) {
          this.pickedTheme = event.theme;
          this.previewImg.clearRequired();
        }
        this.form.patchValue(event);
        this.event = event;

        this.form.get('type').setValue(this.typeFormatter(event.type));

        if (event.invitation.length > 0) {
          let emails = [];
          event.invitation.forEach((el) => {
            if (el.email) {
              emails.push(el.email);
            }
          });
          this.emails = emails;
        }

        if (event.address.latitude) {
          this.form.get('address').patchValue({
            name: event.address.name,
            city: event.address.city,
            voivodeship: event.address.voivodeship,
            country: event.address.country,
            zipcode: event.address.zipcode,
            street: event.address.street,
          });

          this.openStreetMap.updatePosition(event.address.latitude, event.address.longitude);
        }

        if (event.start_at) {
          let startAt = moment(event.start_at, 'YYYY-MM-DD HH:mm', this.storage.getLang());
          this.form.get('startAt').setValue(event.start_at);
          this.form.get('startTime').setValue(startAt.format('HH:mm'));
          this.setStartDate();
        }

        if (event.end_at) {
          this.isEndDate = true;
          let endAt = moment(event.end_at, 'YYYY-MM-DD HH:mm', this.storage.getLang());
          setTimeout(() => {
            this.form.get('endAt').setValue(event.end_at);
            this.form.get('endTime').setValue(endAt.format('HH:mm'));
          }, 500);
        }

        if (event.organization) {
          this.formBeneficent.addControl('checkBeneficent', new UntypedFormControl(''));
          this.formBeneficent.get('checkBeneficent').setValue(true);
          this.isBeneficent = true;

          this.beneficentEditId = event.organization.id;
        }
        this.form.get('categories').setValue(event.categories.map((el) => el.id));
      });
    });
    this.someSubscriptions.add(subData);
  }

  @HostListener('window:beforeunload', ['$event'])
  public checkData() {
    if (this.form.touched) {
      return false;
    }
  }

  categoriesList() {
    if (this.pickedTheme && this.categories.length > 0) {
      this.categoriesFiltered = this.categories.filter((item) => {
        return (
          item.id !==
          (this.pickedTheme.categories != null ? this.pickedTheme.categories.data?.id : null)
        );
      });
      if (this.form.value.categories && this.form.value.categories.length > 0) {
        let { categories } = this.form.value;
        let indexToDelete = null;
        for (let i = 0; i < categories.length; i++) {
          if (categories[i] === this.pickedTheme.categories?.data?.id) {
            indexToDelete = i;
          }
        }
        if (typeof indexToDelete === 'number') {
          categories.splice(indexToDelete, 1);
          this.form.get('categories').setValue(categories);
        }
      }
    } else {
      this.categoriesFiltered = this.categories;
    }
  }

  getCoordinatesFromIp() {
    const subData = this.route.data.subscribe(() => {
      const subGetCoordinates = this.eventService
        .getCoordinates()
        .subscribe((el: Response<Coordinates>) => {
          if (el.data && el.data.latitude && el.data.longitude) {
            this.openStreetMap.updatePosition(+el.data.latitude, +el.data.longitude);
          }
        }, this.onGetCoordinatesError.bind(this));
      this.someSubscriptions.add(subGetCoordinates);
    });
    this.someSubscriptions.add(subData);
  }

  generateTime(hoursStart = 0, minutesStart = 0) {
    let times = [];
    const minutes = ['00', '30'];

    for (let hh = hoursStart; hh < 24; hh++) {
      let time = '';
      if (hh < 10) {
        time += '0';
      }
      time += hh + ':';
      for (let mm = minutesStart; mm < 2; mm++) {
        let timeWithMinutes = time;
        timeWithMinutes += minutes[mm];
        times.push(timeWithMinutes);
      }
    }
    return times;
  }

  setTimes() {
    this.times = this.generateTime();
    this.endTimes = this.generateTime();
  }

  setStartDate() {
    let startAt = moment(this.form.value.startAt, 'YYYY-MM-DD', this.storage.getLang());
    let endAt = moment(this.form.value.endAt, 'YYYY-MM-DD', this.storage.getLang());
    let startAtDayBefore = startAt.subtract(1, 'day');
    let year = parseInt(startAtDayBefore.format('YYYY'));
    let month = parseInt(startAtDayBefore.format('MM'));
    let day = parseInt(startAtDayBefore.format('DD'));

    this.datePickerExtraConfigEndAt.disableUntil.year = year;
    this.datePickerExtraConfigEndAt.disableUntil.month = month;
    this.datePickerExtraConfigEndAt.disableUntil.day = day;

    if (this.form.value.endAt && startAt.isAfter(endAt)) {
      this.form.controls['endAt'].setValue(null);
    }
  }

  startTimeChanged() {
    this.endDateChanged();
  }

  endDateChanged() {
    let startAt = moment(this.form.value.startAt);
    let endAt = moment(this.form.value.endAt);
    if (startAt.isSame(endAt) && this.form.value.startTime) {
      this.form.controls['endTime'].setValue(null);
      let hours = parseInt(this.form.value.startTime.split(':')[0]);
      let minutes = this.form.value.startTime.split(':')[1];

      switch (minutes) {
        case '00':
          minutes = 0;
          break;
        case '30':
          minutes = 1;
          break;
      }

      this.endTimes = this.generateTime(hours, minutes);
    } else {
      this.endTimes = this.generateTime();
    }
  }

  setBeneficent(bool) {
    this.isBeneficent = bool;
    if (!bool && this.form.get('organisation')) {
      this.form.removeControl('organisation');
    }
  }

  setCurrentTheme(index) {
    const current = this.themes[index];
    this.themes.forEach((item) => {
      item.selected = false;
    });

    if (this.pickedTheme === current) {
      this.removeTheme();
      current.selected = false;
    }

    this.previewImg.clearRequired();

    current.selected = true;
    if (!this.form.get('theme')) {
      this.form.addControl('theme', new UntypedFormControl('', Validators.required));
    }
    this.form.controls['theme'].setValue(current.id);
    this.formImg.markAsUntouched();
    this.pickedTheme = current;
    this.categoriesList();
  }

  removeTheme() {
    if (this.phCropper.cropperImg) {
      this.phCropper.cancel();
    }
    this.themes.forEach((item) => {
      item.selected = false;
    });
    this.pickedTheme = null;
    this.previewImg.setRequired();
    this.formImg.controls['previewImg'].setErrors({ incorrect: true });
    this.categoriesList();
    return this.form.removeControl('theme');
  }

  onGetCoordinatesError() {
    this.toast.error(
      this.translate.instant('EVENT.cant_get_coordinates'),
      this.translate.instant('EVENT.event_error')
    );
  }

  getPickedThemeImage() {
    return Config.HOST + this.pickedTheme.image;
  }

  getTags() {
    const subTag = this.eventService.tagList().subscribe((res: Response<Tag[]>) => {
      this.categories = res.data;
      this.categoriesList();
    }, this.onTagError.bind(this));
    this.someSubscriptions.add(subTag);
  }

  enableEndDate() {
    this.isEndDate = true;
  }

  getOrganisations() {
    const organisations = this.eventService
      .organisationsList()
      .subscribe((res: Response<Organisation[]>) => {
        this.organisations = res.data;
        if (this.beneficentEditId) {
          this.form.get('organization').setValue(this.beneficentEditId);
        }
      }, this.onTagError.bind(this));
    this.someSubscriptions.add(organisations);
  }

  getThemes() {
    this.eventService.themeList().subscribe((res: Response<Theme[]>) => {
      this.themes = res.data;
      if (this.pickedTheme) {
        for (let i = 0; i < this.themes.length; i++) {
          if (this.themes[i].id === this.pickedTheme.id) {
            this.setCurrentTheme(i);
          }
        }
      }
    }, this.onThemeError.bind(this));
  }

  getTypes() {
    this.eventService.typeList().subscribe((res: Response<Types[]>) => {
      let types = [];
      const keys = Object.keys(res.data);
      keys.forEach((item) => {
        types.push({
          name: this.translate.instant(`EVENT.${item}`),
          value: res.data[item],
        });
      });
      this.types = types;
    }, this.onTypeError.bind(this));
  }

  get validatePublishProject() {
    // mark all required variable
    this.form.markAllAsTouched();
    if (!this.pickedTheme) {
      this.formImg.markAllAsTouched();
    }
    this.toast.error(
      this.translate.instant('PROJECT.required_publish'),
      this.translate.instant('PROJECT.required_request_error')
    );
    setTimeout(() => {
      this.disabled = false;
    }, 1000);
    return true;
  }

  get isLegal() {
    return this.storage.user.type === 'legal';
  }

  get validatorCreateEvent() {
    this.disabled = true;
    if (this.form.value.description && this.form.value.description.length < 20) {
      this.toast.error(
        this.translate.instant('EVENT.short_description_error'),
        this.translate.instant('PROJECT.required_request_error')
      );
      this.disabled = false;
      return true;
    }
    if (this.form.value.name && this.form.value.name.length < 5) {
      this.toast.error(
        this.translate.instant('EVENT.name_to_short'),
        this.translate.instant('PROJECT.required_request_error')
      );
      this.disabled = false;
      return true;
    }
    if (this.form.value.description && this.form.value.description.length > 1000) {
      this.toast.error(
        this.translate.instant('EVENT.long_description_error'),
        this.translate.instant('PROJECT.required_request_error')
      );
      this.disabled = false;
      return true;
    }
    if (
      !this.form.get('address') ||
      !this.form.get('address.name') ||
      this.form.get('address.name').value === ''
    ) {
      this.form.get('address').reset();
      this.toast.error(
        this.translate.instant('EVENT.missing_location'),
        this.translate.instant('PROJECT.required_request_error')
      );
      this.disabled = false;
      return true;
    }
    if (this.checkUserHaveAllInfo(this.storage.user)) {
      this.additionalInfo.displayAdditionalInfoModal = true;
      return true;
    }
    if (this.checkUserHaveAllYear(this.storage.user)) {
      this.toast.error(
        this.translate.instant('EVENT.error.to_young'),
        this.translate.instant('EVENT.error_to_young_title')
      );
      this.disabled = false;
      return true;
    }
    if (this.form.invalid || (this.isPreviewImgEmpty() && !this.pickedTheme)) {
      return this.validatePublishProject;
    }
    return false;
  }

  isPreviewImgEmpty() {
    return this.formImg.value.previewImg == null;
  }

  unlockButton() {
    this.disabled = false;
  }

  setAddress(event: google.maps.places.PlaceResult | null) {
    this.form.get('address').reset();

    if (event?.address_components) {
      const location = event.geometry.location;

      this.form.get('address.longitude').setValue(location.lng());
      this.form.get('address.latitude').setValue(location.lat());

      this.openStreetMap.updatePosition(location.lat(), location.lng());
      this.form.get('address.name').setValue(event.name);

      event.address_components.forEach((item) => {
        item.types.forEach((type) => {
          if (type === 'premise') {
            this.form.get('address.name').setValue(item.long_name);
          }
          if (type === 'locality') {
            this.form.get('address.city').setValue(item.long_name);
          }
          if (type === 'administrative_area_level_1') {
            this.form.get('address.voivodeship').setValue(item.long_name);
          }
          if (type === 'administrative_area_level_2') {
            this.form.get('address.city').setValue(item.long_name);
          }
          if (type === 'country') {
            this.form.get('address.country').setValue(item.short_name);
          }
          if (type === 'postal_code') {
            this.form.get('address.zipcode').setValue(item.long_name);
          }
          if (type === 'street_number') {
            this.form.get('address.street').setValue(item.long_name);
          }
          if (type === 'route') {
            let number = null;
            if (this.form.get('address.street').value) {
              number = this.form.get('address.street').value;
            }

            this.form.get('address.street').setValue(item.long_name);

            if (number) {
              this.form.get('address.street').setValue(item.long_name + ' ' + number);
            }
          }
        });
      });
    }
  }

  get validateDraftProject() {
    let oneOfIsInvalid = false;
    if (!this.form.controls.name.value) {
      this.form.get('name').markAsTouched();
      this.toast.error(
        this.translate.instant('EVENT.name_required'),
        this.translate.instant('PROJECT.required_request_error')
      );
      oneOfIsInvalid = true;
    }
    if (!this.form.controls.type.value) {
      this.form.get('type').markAsTouched();
      this.toast.error(
        this.translate.instant('EVENT.type_required'),
        this.translate.instant('PROJECT.required_request_error')
      );
      oneOfIsInvalid = true;
    }
    if (this.form.value.name && this.form.value.name.length < 5) {
      this.toast.error(
        this.translate.instant('EVENT.name_to_short'),
        this.translate.instant('PROJECT.required_request_error')
      );
      oneOfIsInvalid = true;
    }
    if (this.form.value.description && this.form.value.description.length > 1000) {
      this.toast.error(
        this.translate.instant('EVENT.long_description_error'),
        this.translate.instant('PROJECT.required_request_error')
      );
      this.disabled = false;
      oneOfIsInvalid = true;
    }

    if (
      !this.form.get('address') ||
      !this.form.get('address.name') ||
      !this.form.get('address.name').value
    ) {
      this.form.get('googleMapsInput').markAsTouched();
      this.form.get('address').setErrors({ incorrect: true });
      this.toast.error(
        this.translate.instant('EVENT.missing_location'),
        this.translate.instant('PROJECT.required_request_error')
      );
      this.disabled = false;
      return true;
    }
    return oneOfIsInvalid;
  }

  createEvent(status: number = EventStatus.ACCEPTED) {
    if (status !== EventStatus.DRAFT) {
      if (this.validatorCreateEvent) {
        return;
      }
    }

    if (status === EventStatus.DRAFT) {
      if (this.validateDraftProject) {
        return;
      }
    }

    if (+this.storage.user.is_active === 2 && status === EventStatus.ACCEPTED) {
      this.toast.error(this.translate.instant('EVENT.cant_add_event_email_ver'));
      this.unlockButton();
      return;
    }

    let newEvent = { ...this.form.getRawValue() };
    if (this.pickedTheme && !newEvent.theme) {
      newEvent.theme = this.pickedTheme.id;
    }
    if (
      ((this.form.value.longitude || this.form.value.latitude) && this.form.get('address')) ||
      (status === EventStatus.DRAFT && this.form.value.longitude)
    ) {
      this.form.get('address.longitude').setValue(this.form.value.longitude);
      this.form.get('address.latitude').setValue(this.form.value.latitude);
    }

    delete newEvent.longitude;
    delete newEvent.latitude;
    delete newEvent.startTime;
    delete newEvent.endTime;
    delete newEvent.googleMapsInput;
    // delete newEvent.phInput;

    let startAt = moment(this.form.value.startAt, 'YYYY-MM-DD');
    let endAt = moment(this.form.value.endAt, 'YYYY-MM-DD');
    let startAtString = startAt.format('DD/MM/YYYY');
    let endAtString = endAt.format('DD/MM/YYYY');

    let startAtFull = moment(
      startAtString + ' ' + this.form.value.startTime,
      'DD/MM/YYYY HH:mm',
      this.storage.getLang()
    );

    let endAtFull = moment(
      endAtString + ' ' + this.form.value.endTime,
      'DD/MM/YYYY HH:mm',
      this.storage.getLang()
    );

    if (this.form.value.startAt) {
      newEvent.startAt = startAtFull.format('YYYY-MM-DD HH:mm');
    }
    if (this.form.value.endAt) {
      newEvent.endAt = endAtFull.format('YYYY-MM-DD HH:mm');
    }

    const pack: Event = new Event({
      ...newEvent,
      invitations: this.emails ? this.emails.join() : null,
    });
    pack.status = status;

    if (!this.editMode) {
      const urlType = this.eventService.newEvent(pack);
      const newEventCreated = urlType.subscribe((res: Response<Event>) => {
        this.sendImg(res.data.id);
      }, this.onCreateEventError.bind(this));
      this.someSubscriptions.add(newEventCreated);
    } else {
      const urlType = this.eventService.editEvent(pack, this.event.id);
      const newEventCreated = urlType.subscribe((res: Response<Event>) => {
        this.sendImg(res.data.id);
      }, this.onCreateEventError.bind(this));
      this.someSubscriptions.add(newEventCreated);
    }
  }

  sendImg(eventId) {
    if (
      this.formImg.value.previewImg &&
      !this.pickedTheme &&
      typeof this.formImg.value.previewImg !== 'string'
    ) {
      const formData = new FormData();
      formData.append('event', eventId.toString());
      formData.append('image', this.formImg.value.previewImg.get('previewImg'));

      const urlType = this.eventService.setImage(formData);
      return urlType.subscribe((res: Response<Event>) => {
        this.eventSuccess(eventId);
      }, this.onCreateEventError.bind(this));
    } else {
      this.eventSuccess(eventId);
    }
  }

  eventSuccess(id) {
    if (this.editMode) {
      this.toast.success(
        this.translate.instant(this.event.edited_at ? 'EVENT.edited' : 'EVENT.accept_event_edited')
      );
    } else {
      this.toast.success(this.translate.instant('EVENT.created'));
    }
    setTimeout(() => {
      this.router.navigate([RoutesPath.default.events + '/' + id]);
    }, 2000);
  }

  onTagError() {
    this.toast.error(
      this.translate.instant('ACCOUNT.tag_cant_get'),
      this.translate.instant('ACCOUNT.tag_error')
    );
  }

  onThemeError() {
    this.toast.error(
      this.translate.instant('ACCOUNT.theme_cant_get'),
      this.translate.instant('ACCOUNT.theme_error')
    );
  }

  onTypeError() {
    this.toast.error(
      this.translate.instant('EVENT.type_cant_get'),
      this.translate.instant('ACCOUNT.type_error')
    );
  }

  onCreateEventError(error: Error) {
    this.disabled = false;
    if (error && error.message === 'user_mail_not_activated') {
      this.toast.error(this.translate.instant('EVENT.cant_add_event_email_ver'));
      let errorMaxCharacters = [];
      if (errorMaxCharacters.length > 0) {
        return;
      }
      this.toast.error(this.translate.instant('EVENT.cant_add_event'));
      return;
    }
  }

  private checkEmailValidation(control: UntypedFormControl) {
    function validateEmail(email) {
      const re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    }
    if (!validateEmail(control.value)) {
      return {
        wrongEmailFormat: true,
      };
    }
    return null;
  }

  emailFieldValidators = [this.checkEmailValidation];

  public errorMessagesCheckEmail = {
    wrongEmailFormat: this.translate.instant('EVENT.wrong_email_format'),
  };

  onUploadError(error: Error) {
    this.toast.error(this.translate.instant('PROJECT.cant_upload_img'));
  }

  findCorrectPhUpload(event: PhCropperOutputFile) {
    this.previewImg.fromCropper(event);
    this.phCropper.closeModal();
  }

  addToCropper(event) {
    this.phCropper.newImg(event);
  }

  get dayNow() {
    const nowDay = moment().subtract(1, 'days');
    return {
      year: parseInt(nowDay.format('YYYY'), 10),
      month: parseInt(nowDay.format('M'), 10),
      day: parseInt(nowDay.format('D'), 10),
    };
  }
}
